// Časová osa příchodů/odchodů: plošný graf počtu přítomných v čase + seskupení
// do časových slotů s počty příchodů (IN) a odchodů (OUT).

function presentSeries(data) {
  const last = {};
  let present = 0;
  const points = [];
  for (const r of data) {
    const prev = last[r.member_id];
    if (r.status === 'IN' && prev !== 'IN') present++;
    else if (r.status === 'OUT' && prev === 'IN') present--;
    last[r.member_id] = r.status;
    points.push({ ts: new Date(r.timestamp).getTime(), count: present });
  }
  return points;
}

// Zvolí velikost slotu tak, aby vyšlo cca 6–20 slotů.
function chooseSlotMs(spanMs) {
  const candidates = [5, 10, 15, 30, 60, 120, 240, 360, 720, 1440].map((m) => m * 60000);
  for (const c of candidates) if (spanMs / c <= 16) return c;
  return candidates[candidates.length - 1];
}

function buildSlots(data) {
  if (data.length === 0) return [];
  const times = data.map((r) => new Date(r.timestamp).getTime());
  const min = Math.min(...times), max = Math.max(...times);
  const slotMs = chooseSlotMs(Math.max(max - min, 1));
  const map = new Map();
  const last = {};
  let present = 0;
  for (const r of data) {
    const t = new Date(r.timestamp).getTime();
    const slot = Math.floor(t / slotMs) * slotMs;
    if (!map.has(slot)) map.set(slot, { slot, in: 0, out: 0, present: 0 });
    const s = map.get(slot);
    const prev = last[r.member_id];
    if (r.status === 'IN') { s.in++; if (prev !== 'IN') present++; }
    else if (r.status === 'OUT') { s.out++; if (prev === 'IN') present--; }
    last[r.member_id] = r.status;
    s.present = present;
  }
  return [...map.values()].sort((a, b) => a.slot - b.slot).map((s) => ({ ...s, slotMs }));
}

const fmtTime = (ms) => new Date(ms).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
const fmtRange = (s) => `${fmtTime(s.slot)}–${fmtTime(s.slot + s.slotMs)}`;

export default function AttendanceTimeline({ data }) {
  if (!data || data.length === 0) return <p className="text-muted text-center">Zatím žádné příchody ani odchody.</p>;

  const points = presentSeries(data);
  const slots = buildSlots(data);
  const minTs = points[0].ts, maxTs = points[points.length - 1].ts;
  const maxCount = Math.max(...points.map((p) => p.count), 1);
  const tsDiff = (maxTs - minTs) || 1;

  const W = 680, H = 220, PAD = { top: 16, right: 20, bottom: 36, left: 40 };
  const chartW = W - PAD.left - PAD.right, chartH = H - PAD.top - PAD.bottom;
  const px = (ts) => PAD.left + ((ts - minTs) / tsDiff) * chartW;
  const py = (c) => PAD.top + chartH - (c / maxCount) * chartH;

  let d = '';
  points.forEach((p, i) => {
    const x = px(p.ts), y = py(p.count);
    if (i === 0) d += `M${x},${py(0)} L${x},${y}`;
    else d += ` L${x},${py(points[i - 1].count)} L${x},${y}`;
  });
  d += ` L${px(maxTs)},${py(0)} Z`;

  const yTicks = [];
  const step = Math.ceil(maxCount / 4) || 1;
  for (let v = 0; v <= maxCount; v += step) yTicks.push(v);

  const xLabels = [];
  const n = Math.min(5, points.length);
  for (let i = 0; i < n; i++) {
    const idx = n === 1 ? 0 : Math.round((i / (n - 1)) * (points.length - 1));
    xLabels.push({ x: px(points[idx].ts), label: fmtTime(points[idx].ts) });
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={PAD.left} y1={py(v)} x2={PAD.left + chartW} y2={py(v)} stroke="var(--color-border)" strokeWidth="1" />
              <text x={PAD.left - 6} y={py(v) + 4} textAnchor="end" fontSize="11" fill="var(--color-text-muted, #888)">{v}</text>
            </g>
          ))}
          <path d={d} fill="var(--color-primary)" fillOpacity="0.15" stroke="var(--color-primary)" strokeWidth="2" />
          {xLabels.map((l, i) => (
            <text key={i} x={l.x} y={H - PAD.bottom + 18} textAnchor="middle" fontSize="11" fill="var(--color-text-muted, #888)">{l.label}</text>
          ))}
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="var(--color-border)" />
          <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="var(--color-border)" />
        </svg>
      </div>
      <p className="text-muted text-center" style={{ fontSize: '0.8rem', marginTop: -4 }}>Počet přítomných osob v čase</p>

      <div className="table-wrapper" style={{ marginTop: 12 }}>
        <table>
          <thead><tr><th>Časový slot</th><th style={{ textAlign: 'right' }}>Příchody</th><th style={{ textAlign: 'right' }}>Odchody</th><th style={{ textAlign: 'right' }}>Přítomno</th></tr></thead>
          <tbody>
            {slots.map((s) => (
              <tr key={s.slot}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtRange(s)}</td>
                <td style={{ textAlign: 'right' }}>{s.in > 0 ? <strong style={{ color: 'var(--color-success, green)' }}>+{s.in}</strong> : <span className="text-muted">—</span>}</td>
                <td style={{ textAlign: 'right' }}>{s.out > 0 ? <strong style={{ color: 'var(--color-danger, #c0392b)' }}>−{s.out}</strong> : <span className="text-muted">—</span>}</td>
                <td style={{ textAlign: 'right' }} className="text-muted">{s.present}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
